import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Communication } from "../../../data/Communication";
import { useGlobal } from "../../../utils/GlobalContext";
import useCommunicationTypes from "../../communicationTypes/hooks/useCommunicationTypes";
import { getEventInfo } from "../../../services/EventInformation";
import { EventsListItem } from "../../../data/eventInfo";
import IconList from "../../../data/account/socialMediaIcons";
import 'react-quill/dist/quill.snow.css';
import Editor from "../../../utils/RTEditor";
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    selectedCommunication: Communication | null;
    onSave: (updatedType: {
        communicationId: string;
        title: string;
        comTypeId: string;
        eventName: string;
        status: string;
        composedTimeStamp: string;
        reviewedTimeStamp: string;
        reviewedBy: string;
        sendDateAsString: string;
        message: string;
    }) => Promise<void>;
}

const EditModal: React.FC<Props> = ({ isOpen, onClose, selectedCommunication, onSave }) => {
    const [communicationId, setCommunicationId] = useState('');
    const [title, setTitle] = useState('');
    const [comTypeId, setComTypeId] = useState('');
    const [status, setStatus] = useState('');
    const [composedBy, setComposedBy] = useState('');
    const [composedTimeStamp, setComposedTs] = useState('');
    const [reviewedBy, setReviewedBy] = useState('');
    const [reviewedTimeStamp, setReviewedTs] = useState('');
    const [message, setMessage] = useState('');
    const [events, setEvents] = useState<EventsListItem[]>([]);
    const [eventName, setEventName] = useState('');
    const [sendDate, setSendDate] = useState<Dayjs | null>(null);
    const [sendDateAsString, setSendDateAsString] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { state } = useGlobal();
    const { communicationTypes } = useCommunicationTypes();
    const fullName = `${state.UserProfile.FirstName} ${state.UserProfile.LastName}`;

    useEffect(() => {
        setReviewedTs(new Date().toISOString());
        const fetchEvents = async () => {
            try {
                const eventList = await getEventInfo();
                setEvents(eventList);
            } catch (error) {
                console.error("Error fetching events:", error);
            }
        };
        fetchEvents();
    }, [isOpen]);

    useEffect(() => {
        if (selectedCommunication) {
            setCommunicationId(selectedCommunication.communicationId);
            setTitle(selectedCommunication.title);
            setEventName(selectedCommunication.eventName);
            setComTypeId(selectedCommunication.comTypeId);
            setStatus(selectedCommunication.status);
            setComposedBy(selectedCommunication.composedBy);
            setComposedTs(selectedCommunication.composedTimeStamp);
            setSendDateAsString(selectedCommunication.sendDate);
            setSendDate(selectedCommunication.sendDate ? dayjs(selectedCommunication.sendDate) : null);
            setMessage(selectedCommunication.message);

            if (fullName !== selectedCommunication.composedBy) {
                setReviewedBy(fullName);
            } else {
                setReviewedBy('');
            }
        }
    }, [selectedCommunication, fullName]);

    const handleSave = async () => {
        if (!selectedCommunication) return;
        setIsSaving(true);
        try {
            await onSave({
                communicationId,
                title,
                comTypeId,
                eventName,
                status,
                composedTimeStamp, // ISO string
                reviewedTimeStamp, // ISO string
                reviewedBy,
                sendDateAsString,
                message,
            });
            onClose();
        } catch (error) {
            console.error("Save failed", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDateChange = (newValue: Dayjs | null) => {
        setSendDate(newValue);
        setSendDateAsString(newValue ? newValue.toISOString() : '');
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box sx={{
                position: 'relative',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '95vw',
                height: '95vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 3,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} margin="normal" />

                <Box display="flex" gap={2} sx={{ width: '100%' }}>
                    <FormControl fullWidth margin="normal" sx={{ flex: 1 }}>
                        <InputLabel>Event ID</InputLabel>
                        <Select value={eventName} onChange={(e) => setEventName(e.target.value)} label="Event ID">
                            {events.map((event) => (
                                <MenuItem key={event.Name} value={event.Name}>
                                    {event.Name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal" sx={{ flex: 1 }}>
                        <InputLabel>Communication Type</InputLabel>
                        <Select value={comTypeId} onChange={(e) => setComTypeId(e.target.value)} label="Communication Type">
                            {communicationTypes.map((type) => {
                                const IconComponent = IconList[type.icon as keyof typeof IconList];
                                return (
                                    <MenuItem key={type.comTypeId} value={type.comTypeId}>
                                        <Box display="flex" alignItems="center">
                                            {IconComponent && <IconComponent style={{ marginRight: 8 }} />}
                                            {type.comType}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>

                <Box display="flex" gap={2} alignItems="center">
                    <Box flex={1}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                                <MenuItem value="Draft">Draft</MenuItem>
                                <MenuItem value="Ready">Ready</MenuItem>
                                <MenuItem value="Sent">Sent</MenuItem>
                                <MenuItem value="Reject">Reject</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box flex={1}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DateTimePicker']}>
                                <DateTimePicker
                                    label="Send Date"
                                    value={sendDate}
                                    onChange={handleDateChange}
                                    sx={{ width: '90%', minWidth: '200px' }}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Box>
                </Box>

                <Grid item xs={12}>
                    <Typography variant="h6" component="h2" textAlign="center">Message</Typography>
                    <div className="EditorContainer">
                        <Editor editorName="editor" content={message} saveContent={setMessage} showSaveButton={false} />
                    </div>
                </Grid>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving || !reviewedBy}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ mt: 2 }}
                >
                    {isSaving ? "Saving..." : "Save"}
                </Button>
            </Box>
        </Modal>
    );
};

export default EditModal;
